import { defineStore } from 'pinia';

export const usePodcastManageStore = defineStore('podcastManage', {
  state: () => ({
    podcast: {
      uuid: null,
      items: [],
      slug: null,
      name: null,
      description: null,
      external_links: {},
      feed_type: 1,
      update_type: 1,
      has_changes: 0,
      auto_sync_items_by_tags: 0,
      sync_to_website: 0,
      import_url: null,
      status: 1,
      ordering: 1,
      created_at: null,
      updated_at: null,
      itunes_detail: {
        feed_id: null,
        type: 'serial',
        summary: null,
        language: null,
        copyright: null,
        managing_editor: null,
        webmaster: null,
        publish_date: null,
        itunes_category_id: null,
        itunes_subcategory_id: null,
        image_url: null,
        author: null,
        explicit: 'no',
        docs: null,
        link: null,
        new_feed_url: null,
        owner_name: null,
        created_at: null,
        updated_at: null,
      },
      tags: [],
      people: [],
      artwork_id: null,
      artwork: null,
      artwork_uuid: null,
      banner_artwork_id: null,
      banner_artwork: null,
      banner_artwork_uuid: null,
      podcast_website: {
        enabled: false,
        id: null,
        feed_id: null,
        name: null,
        description: null,
        url: null,
      },
      storage_url_prefix: null,
    },
    episodes: [],
    loading_episodes: false,
    original_podcast_data: null,
    explicit_options: [
      {
        display: 'Yes',
        value: 'yes',
      },
      {
        display: 'Clean',
        value: 'clean',
      },
    ],
    type_options: [
      {
        display: 'Serial',
        value: 'serial',
      },
      {
        display: 'Episodic',
        value: 'episodic',
      },
    ],
    ordering_options: [
      {
        display: 'Newest Added',
        value: 1,
      },
      {
        display: 'Oldest Added',
        value: 2,
      },
      {
        display: 'Published Newest',
        value: 3,
      },
      {
        display: 'Published Oldest',
        value: 4,
      },
      {
        display: 'Random',
        value: 6,
      },
    ],
    language_codes: window.language_codes,
    active_tab: 1,
    tab_map: {
      episodes: 0,
      settings: 1,
      website: 2,
      people: 3,
      syndication: 4,
      sharing: 5,
    },
    ms_tag_values: [],
    auto_complete_tags: [],
    possible_external_links: [],
    website_loading: false,
    loading: false,
    preserve_asset_on_episode_delete: false,
  }),
  actions: {
    setArtwork(selected_artwork) {
      this.podcast.artwork = selected_artwork;
      this.podcast.artwork_uuid = selected_artwork.uuid;
    },
    setBanner(selected_artwork) {
      this.podcast.banner_artwork = selected_artwork;
      this.podcast.banner_artwork_uuid = selected_artwork.uuid;
    },
    setPodcastData(the_podcast) {
      const default_itunes_details = { ...this.podcast.itunes_detail };
      this.podcast = the_podcast;
      // Create copy of the original podcast data before it was modified so we can track changes.
      this.original_podcast_data = { ...the_podcast };

      // This can happen if a plan has been moved from a broadcaster to a podcaster.
      if (!this.podcast.itunes_detail) {
        this.podcast.itunes_detail = default_itunes_details;
      }

      // The multi-selects and radios need a different value to that which is set in the database.
      // As such we need to set them correctly.
      this.setupExternalLinks();
      this.setExplicitOption();
      this.setTypeOption();
      this.setOrderingOption();
      this.setLanguageCode();
      this.setupPodcastWebsite();
      this.ms_tag_values = this.podcast.tags.map(tag => tag.name);
    },
    setDefaults() {
      this.original_podcast_data = { ...this.podcast };
      this.setExplicitOption();
      this.setTypeOption();
      this.setOrderingOption();
    },
    setLanguageCode() {
      if (this.podcast.itunes_detail?.language) {
        this.podcast.itunes_detail.language = _.find(this.language_codes, language => language.code === this.podcast.itunes_detail.language);
      }
    },
    setExplicitOption() {
      if (!this.podcast.itunes_detail.explicit) {
        this.explicit_options[0].checked = true;
        return;
      }

      // Hack because we removed 'no' from options
      if (this.podcast.itunes_detail.explicit === 'no') {
        this.podcast.itunes_detail.explicit = 'clean';
      }

      const explicit_option = _.find(this.explicit_options, {
        'value': this.podcast.itunes_detail.explicit,
      });
      explicit_option.checked = true;
    },
    setTypeOption() {
      if (!this.podcast.itunes_detail.type) {
        this.type_options[0].checked = true;
        return;
      }
      const type_option = _.find(this.type_options, {
        'value': this.podcast.itunes_detail.type,
      });
      type_option.checked = true;
    },
    setOrderingOption() {
      const ordering_option = this.ordering_options.find(opt => opt.value === this.podcast.ordering) ?? this.ordering_options[0];
      ordering_option.checked = true;
    },
    async deleteEpisode(episode) {
      try {
        const url = new URL(`${window.location.origin}/api/v1/items/${episode.uuid}`);
        if (this.preserve_asset_on_episode_delete) {
          url.searchParams.append('preserve-asset', true);
        }

        await axios.delete(url.href);
        toastr.success('Episode Deleted');
        this.getEpisodes();
      } catch (error) {
        window.handleAxiosError(error);
      }
    },
    async getEpisodes() {
      if (!this.podcast.uuid) {
        this.episodes = [];
      }

      try {
        this.loading_episodes = true;
        this.episodes = [];
        const response = await axios.get(`/api/v1/items?feed_uuid=${this.podcast.uuid}&with_player_data=true&per_page=10000`);
        this.episodes = response.data.data;

        this.loading_episodes = false;
      } catch (error) {
        toastr.error("Unable to retrieve items for Podcast.");
        this.loading_episodes = false;
      }
    },
    async validateAndSave(validator, update = false) {
      try {
        const valid = await validator.validateAll('general-form');
        if (!valid) {
          this.setActiveTab('settings');
          toastr.error("Some fields in the general settings have not been populated correctly. Please fix and try again.");
          return false;
        }
      } catch (error) {
        console.error(error);
      }

      try {
        const valid = await validator.validateAll('discovery-form');
        if (!valid) {
          this.setActiveTab('settings');
          toastr.error("Some fields in the discovery settings have not been populated correctly. Please fix and try again.");
          return false;
        }
      } catch (error) {
        console.error(error);
      }

      try {
        const valid = await validator.validateAll('author-details-form');
        if (!valid) {
          this.setActiveTab('settings');
          toastr.error("Some fields in the author details settings have not been populated correctly. Please fix and try again.");
          return false;
        }
      } catch (error) {
        console.error(error);
      }

      try {
        const valid = await validator.validateAll('advanced-form');
        if (!valid) {
          this.setActiveTab('settings');
          toastr.error("Some fields in the advanced settings have not been populated correctly. Please fix and try again.");
          return false;
        }
      } catch (error) {
        console.error(error);
      }

      try {
        const valid = await validator.validateAll('external-links');
        if (!valid) {
          this.setActiveTab('syndication');
          toastr.error("Some fields in the syndication settings have not been populated correctly. Please fix and try again.");
          return false;
        }
      } catch (error) {
        console.error(error);
      }

      // Create non-reactive copy of podcast. Trust me, has to be done this way.
      const data = JSON.parse(JSON.stringify(this.podcast));
      // Make amendments required for patch method.
      data.itunes_detail.language = data.itunes_detail.language?.code ?? null;
      data.tags = this.ms_tag_values;
      data.people = this.podcast.people;

      if (update) {
        try {
          const response = await axios.patch(`/api/v1/podcasts/${this.podcast.uuid}`, data);
          toastr.success('Podcast Saved.');
          this.setPodcastData(response.data.data);
        } catch (error) {
          window.handleAxiosError(error);
        }
      } else {
        try {
          const response = await axios.post(`/api/v1/podcasts`, data);
          toastr.success('Podcast Saved. Redirecting you to your new Podcast...');
          window.location.href = `/feeds/${response.data.data.uuid}/edit#episodes`;
          // You could continue by running the below - but we have chosen to redirect for the purposes of being on the correct URL
          // this.setPodcastData(response.data.data);
          // this.setActiveTab('episodes');
        } catch (error) {
          window.handleAxiosError(error);
        }
      }

      return true;
    },
    setActiveTab(tab) {
      if (this.creating) {
        this.active_tab = 0;
        return;
      }

      // Default tab should be episodes, unless we are creating a new podcast, can't add episodes to a podcast that isn't created.
      const default_tab = this.tab_map.episodes;

      // If we are passing a specific tab to this method then just set that tab.
      if (tab && this.tab_map[tab]) {
        // Can't use episodes tab if creating a podcast.
        if (tab === 'episodes' && this.creating) {
          this.active_tab = default_tab;
          return;
        }

        this.active_tab = this.tab_map[tab];
        return;
      }

      const current_hash = window.location.hash.replace('#', '');
      if (current_hash) {
        // If there is an anchor in the url and it matches our tab map then select it.
        this.active_tab = this.tab_map[current_hash] ?? default_tab;
        return;
      }
      // Always fallback to episodes
      this.active_tab = default_tab;
    },
    async getAllTags() {
      try {
        this.auto_complete_tags = (await axios.get(`/api/v1/tags?per_page=1000`)).data.data?.map(tag => tag.name);
      } catch (error) {
        window.handleAxiosError(error);
      }
    },
    addTag(tag) {
      tag = tag.toLowerCase();
      this.auto_complete_tags.push(tag);
      this.ms_tag_values.push(tag);
    },
    async togglePodcastWebsite() {
      if (!this.podcast.podcast_website) {
        this.setPodcastWebsiteDefaults();
      }

      this.website_loading = true;
      if (!this.podcast.podcast_website.enabled) {
        try {
          const response = await axios.post(`/api/v1/podcast-websites/${this.podcast.uuid}`);
          this.podcast.podcast_website = response.data.data;
          this.podcast.podcast_website.enabled = true;
          toastr.success("Successfully enabled Website for Podcast.");
        } catch (error) {
          this.podcast.podcast_website.enabled = false;
          window.handleAxiosError(error);
        }
      } else {
        try {
          await axios.delete(`/api/v1/podcast-websites/${this.podcast.podcast_website.id}`);
          this.setPodcastWebsiteDefaults();
          toastr.success("Successfully removed Website for Podcast.");
        } catch (error) {
          this.podcast.podcast_website.enabled = true;
          window.handleAxiosError(error);
        }
      }
      this.website_loading = false;
    },
    setPodcastWebsiteDefaults() {
      this.podcast.podcast_website = {
        enabled: false,
        feed_id: this.podcast.id,
        name: null,
        description: null,
      };
    },
    async setupPodcastWebsite() {
      if (this.podcast.podcast_website?.feed_id) {
        this.podcast.podcast_website.enabled = true;
      } else {
        this.setPodcastWebsiteDefaults();
      }
    },
    async setupExternalLinks() {
      if (window.external_link_templates) {
        this.possible_external_links = window.external_link_templates;
      }

      if (!this.possible_external_links) {
        try {
          const response = await axios.get(`/api/v1/feeds/${this.podcast.uuid}/external-link-templates`);
          this.possible_external_links = response.data.data;
        } catch (error) {
          toastr.error("Unable to setup Syndication. Please contact SharpStream Support");
          return;
        }
      }

      _.forEach(this.podcast.external_links, (external_link) => {
        const possible_link = _.find(this.possible_external_links, {
          id: external_link.id,
        });
        if (possible_link) {
          possible_link.url = external_link.url;
        }
      });

      this.podcast.external_links = this.possible_external_links;
    },
  },
  getters: {
    /**
     * This can be used as a computed property to track changed to the podcast name
     * @see resources/assets/js/partials/Feeds/PodcastSettings/General.vue
     * @returns {function(*): boolean}
     */
    podcastPropertyHasChanged: state => property => JSON.stringify(state.podcast[property]) !== JSON.stringify(state.original_podcast_data[property]),
    creating() {
      return !this.podcast.uuid;
    },
  },
});
